import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import Spinner from "../../Components/Spinner";
import DataGridTable from "../../Components/DataGridTable";
import { BASE_URL_API } from "../../Constants";
import { useAuth } from "../../hooks/AuthProvider";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";

import { LanguageContext } from "../../context/LanguageContext";
import "./UserHealthDetails.css";
import {
  getHealthTranslations,
  HealthPageHeader,
  PermissionDenied,
  isRtlLanguage,
} from "./userHealthShared";

export const MedicalDevices = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { permissions } = useAuth();

  const { lang } = useContext(LanguageContext);
  const t = getHealthTranslations(lang);

  const canView = permissions["view-medical-devices"];

  const isRtl = isRtlLanguage(lang);
  const canFinishScan = permissions["health_device_finish_scan"];

  const pagingoptions = [10, 20, 50, 100];

  const fetchData = useCallback(async () => {
    if (!canView) return;

    setLoading(true);

    try {
      const res = await axios.get(`${BASE_URL_API}/medical-device/list`);
      const payload = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data?.DATA)
          ? res.data.DATA
          : [];

      if (Array.isArray(payload)) {
        const formatted = payload.map((item) => ({
          ...item,
          userName: item.user?.name || item.userName || "-",
        }));

        setRows(formatted);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("MedicalDevices fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  const markAvailable = useCallback(async (row) => {
    try {
      setLoading(true);
      await axios.post(`${BASE_URL_API}/medical-device/finish-scan`, {
        healthDeviceId: row.id,
      });
      await fetchData();
    } catch (err) {
      console.error("MedicalDevices finish scan error:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  const columns = [
    ...(canFinishScan ? [{
      field: "actions",
      headerName: "",
      type: "actions",
      width: 80,
      getActions: (params) => {
        const isScanning = String(params.row.scanStatus || "").toLowerCase() === "scanning";
        if (!isScanning) return [];

        return [
          <GridActionsCellItem
            key="mark-available"
            icon={
              <Tooltip title={t.actions.markAvailable}>
                <CheckCircleOutlineIcon
                  style={{
                    color: "var(--health-success-color)",
                    fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
                  }}
                />
              </Tooltip>
            }
            label={t.actions.markAvailable}
            showInMenu
            onClick={() => markAvailable(params.row)}
          />,
        ];
      },
    }] : []),
    {
      field: "userName",
      headerName: t.columns.user,
      width: 250,
      align: "center",
      headerAlign: "center"

    },
    {
      field: "scanStatus",
      headerName: t.columns.scanStatus,
      width: 150,
      align: "center",
      headerAlign: "center"
,
      renderCell: (params) => {
        const raw = String(params.row.scanStatus || "").toLowerCase();
        const label = raw === "scanning"
          ? t.labels.scanning
          : raw === "avaliable" || raw === "available"
            ? t.labels.available
            : (params.row.scanStatus || t.labels.unknown);
        return (
          <span>
            <span className={`health-status-dot ${raw === "scanning" ? "scanning" : "available"}`} />
            {label}
          </span>
        );
      }
    },
  
  ];
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!canView) {
    return (
      <PermissionDenied title={t.states.permissionDenied} message={t.states.cannotViewHealth} />
    );
  }

  return (
    <div className="configuration devices-page health-page" dir={isRtl ? "rtl" : "ltr"} style={{ height: "100%" }}>
      <div className="device-main health-page-shell">
        <HealthPageHeader
          title={t.pages.medicalDevices}
          subtitle={t.descriptions.medicalDevices}
        />

        {loading ? (
          <Spinner />
        ) : rows.length > 0 ? (
          <div className="table-wrapper" style={{ flex: 1 }}>
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn={"id"}
              pagingoptions={pagingoptions}
              sortField="created_at"
              sortType="desc"
              showToolbar={true}
              tableKey="medical_devices"

            />
          </div>
        ) : (
          <div className="health-empty-state">{t.states.noData}</div>
        )}
      </div>
    </div>
  );
};
